/**
 * @module hooks/useIdleTimer
 * @description Hook React que detecta inactividad del usuario para activar el modo ambient.
 *
 * Escucha eventos del DOM (mousemove, mousedown, keydown, touchstart, scroll, wheel)
 * y reinicia un timeout en cada interacción. Si el timeout expira sin actividad,
 * llama a `onIdle`. Cuando el usuario vuelve a interactuar, llama a `onActive`.
 *
 * ## Patrón de referencia estable
 * Usa `optionsRef` (actualizado en cada render vía `useLayoutEffect`) para que el
 * callback de eventos siempre acceda a las opciones actuales sin cambiar la referencia
 * de la función. Esto evita acumulación de listeners en re-renders.
 *
 * ## Activación/desactivación
 * Si `options.enabled = false`, el hook desconecta todos los listeners y limpia
 * el timeout. Útil para pausar la detección mientras el modo ambient ya está activo.
 *
 * @see AmbientModeContext para el consumidor principal de este hook
 * @see IdleTimerOptions en types/ambient.ts para la interfaz de opciones
 */
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import type { IdleTimerOptions } from '../types/ambient';

const DEFAULT_EVENTS: (keyof DocumentEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'wheel',
];

/**
 * Hook que detecta inactividad del usuario y ejecuta callbacks cuando el
 * usuario queda idle o vuelve a estar activo.
 *
 * Usa una referencia estable del handler (useCallback con deps vacíos) para
 * que addEventListener/removeEventListener funcionen en par correcto y no
 * acumulen listeners en cada render.
 */
export function useIdleTimer(options: IdleTimerOptions): void {
  // Referencia a las opciones actuales — se actualiza en cada render sin
  // cambiar la referencia del callback (evita stale closures).
  const optionsRef = useRef<IdleTimerOptions>(options);
  useLayoutEffect(() => {
    optionsRef.current = options;
  });

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isIdleRef = useRef(false);

  // Referencia estable — misma función en memoria durante toda la vida del hook.
  const resetTimer = useCallback(() => {
    const { timeoutMs, onIdle, onActive } = optionsRef.current;

    // Si el usuario interactúa mientras estaba idle → notificar
    if (isIdleRef.current) {
      isIdleRef.current = false;
      onActive?.();
    }

    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      isIdleRef.current = true;
      optionsRef.current.onIdle();
    }, timeoutMs);
  }, []); // deps vacíos → referencia estable

  useEffect(() => {
    if (!options.enabled) {
      // Desactivado: limpiar timer y no escuchar eventos
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      timerRef.current = null;
      return;
    }

    const events = optionsRef.current.events.length > 0
      ? optionsRef.current.events
      : DEFAULT_EVENTS;

    // Arrancar el contador inmediatamente
    resetTimer();

    // Adjuntar listeners con referencia estable (se pueden remover exactamente)
    events.forEach(event =>
      document.addEventListener(event, resetTimer, { passive: true })
    );

    // Page Visibility API: pausar cuando la pestaña está oculta
    const handleVisibility = () => {
      if (document.hidden) {
        if (timerRef.current !== null) clearTimeout(timerRef.current);
      } else {
        // Al volver, reiniciar desde cero (no contar tiempo oculto)
        resetTimer();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      if (timerRef.current !== null) clearTimeout(timerRef.current);
      events.forEach(event =>
        document.removeEventListener(event, resetTimer)
      );
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [options.enabled, resetTimer]);
}

export { DEFAULT_EVENTS };
